/* =============================================================================
   TTW Award v5.2: the few effects CSS cannot do alone. Declarative, one
   attribute per effect:

     data-aw="mask"      media wipes in behind a moving clip edge, once
     data-aw="magnet"    the HERO CTA leans toward a fine pointer. Honoured only
                         on a [data-ttw-hero-cta] or inside the home hero
                         (.ttw-hero3); anywhere else the attribute is ignored.
     data-aw="marquee"   seamless infinite ticker
     data-aw-progress    thin scroll-progress bar. CSS scroll(root) drives it
                         where supported (ttw-award.css); this is the fallback.

   Retired in v5.2: tilt, lines, split, the old plugin registration and the
   kinetic-headline safety net. Kinetic headings, reveals, parallax and draws
   are native CSS now (ttw-award.css + ttw-motion.js), and nothing is hidden
   before paint any more, so there is nothing left to rescue.

   NOTHING here hides content in CSS. Every start state is applied by this
   script, only once it runs. If it fails to parse, if the visitor prefers
   reduced motion or if the browser is too old, every element is simply
   already in its final, readable state.
   ============================================================================= */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var killed = root.hasAttribute('data-ttw-motion-off') || window.TTW_MOTION_OFF === true;
  if (reduce || killed) return;

  /* ── 1. Mask reveal ────────────────────────────────────────────────────────
     A clip edge travels across the image. inset() rather than opacity, so the
     image never looks washed out mid-flight. */
  function initMask(el) {
    if (el.__aw) return; el.__aw = 1;
    var dir = el.getAttribute('data-aw-dir') || 'up';
    var from = { up: 'inset(100% 0 0 0)', down: 'inset(0 0 100% 0)',
                 left: 'inset(0 100% 0 0)', right: 'inset(0 0 0 100%)' }[dir] || 'inset(100% 0 0 0)';

    /* Do NOT clip a hidden tab. This effect starts by hiding its element, and in
       a backgrounded tab neither the observer nor the setTimeout net can be
       relied on to bring it back - rAF is suspended and timers are throttled to
       about once a minute. Wait for the tab to be looked at. */
    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', function onVis() {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', onVis);
          el.__aw = 0;
          initMask(el);
        }
      });
      return;
    }
    el.style.clipPath = from;
    el.style.webkitClipPath = from;
    /* A flag, not a string compare: the browser re-serialises the inline value
       ("inset(100% 0 0 0)" reads back as "inset(100% 0px 0px)"), so comparing
       strings never matched and the safety net below never fired. */
    var played = false;
    var play = function () {
      if (played) return; played = true;
      el.style.transition = 'clip-path 1.05s cubic-bezier(0.16,1,0.3,1), -webkit-clip-path 1.05s cubic-bezier(0.16,1,0.3,1)';
      el.style.clipPath = 'inset(0 0 0 0)';
      el.style.webkitClipPath = 'inset(0 0 0 0)';
    };
    /* Observe the unclipped parent: a target clipped to nothing never reports
       as intersecting, so watching the element itself could leave it hidden. */
    observe(el.parentElement || el, play);
    /* If the observer never delivers, the image must still appear. */
    setTimeout(function () {
      play();
    }, 2500);
    document.addEventListener('visibilitychange', function onVis() {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVis);
        play();
      }
    });
  }

  /* ── 2. Hero magnet ────────────────────────────────────────────────────────
     Fine pointers only. Touch gets nothing, which is correct: there is no hover
     to lean into, and a transform mid-tap moves the target under a finger. It
     writes `transform`; the :active press uses `translate`, so they compose. */
  function initMagnet(el) {
    if (el.__aw) return; el.__aw = 1;
    if (!el.matches('[data-ttw-hero-cta]') && !(el.closest && el.closest('.ttw-hero3'))) return;
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var strength = parseFloat(el.getAttribute('data-aw-strength')) || 0.28;
    var raf = null, tx = 0, ty = 0;
    function apply() { raf = null; el.style.transform = 'translate(' + tx + 'px,' + ty + 'px)'; }
    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      tx = (e.clientX - (r.left + r.width / 2)) * strength;
      ty = (e.clientY - (r.top + r.height / 2)) * strength;
      if (!raf) raf = requestAnimationFrame(apply);
    });
    el.addEventListener('pointerleave', function () {
      tx = ty = 0;
      el.style.transition = 'transform .45s cubic-bezier(0.34,1.56,0.64,1)';
      if (!raf) raf = requestAnimationFrame(apply);
      setTimeout(function () { el.style.transition = ''; }, 460);
    });
  }

  /* ── 3. Marquee ────────────────────────────────────────────────────────────
     Duplicates its own children until the track is at least twice the viewport,
     then translates by exactly half, so the loop seam is invisible at any width. */
  function initMarquee(el) {
    if (el.__aw) return; el.__aw = 1;
    var track = el.querySelector('[data-aw-track]');
    if (!track) return;
    var original = track.innerHTML;
    var guard = 0;
    while (track.scrollWidth < window.innerWidth * 2 && guard++ < 12) {
      track.innerHTML += original;
    }
    track.innerHTML += track.innerHTML;          /* the half we translate by */
    var speed = parseFloat(el.getAttribute('data-aw-speed')) || 42;
    var dur = track.scrollWidth / 2 / speed;
    track.style.animation = 'aw-marquee ' + dur.toFixed(1) + 's linear infinite';
  }

  /* ── 4. Scroll progress (fallback only) ────────────────────────────────────*/
  function initProgress(el) {
    if (el.__aw) return; el.__aw = 1;
    if (window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()')) return;
    var bar = el.querySelector('[data-aw-progress-bar]') || el;
    var ticking = false;
    function set() {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0;
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    }
    addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(set); }
    }, { passive: true });
    addEventListener('resize', set, { passive: true });
    set();
  }

  /* ── Shared observer ───────────────────────────────────────────────────────*/
  function observe(el, cb) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { cb(); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    io.observe(el);
  }

  var HANDLERS = { mask: initMask, magnet: initMagnet, marquee: initMarquee };

  function scan(scope) {
    (scope || document).querySelectorAll('[data-aw]').forEach(function (el) {
      var fn = HANDLERS[el.getAttribute('data-aw')];
      if (fn) { try { fn(el); } catch (e) { /* one broken effect must never stop the rest */ } }
    });
    (scope || document).querySelectorAll('[data-aw-progress]').forEach(initProgress);
  }

  function boot() {
    scan(document);
    root.setAttribute('data-aw-ready', '');
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) { scan(e.target || document); });
})();
